import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useField, useFormikContext } from 'formik';
import { Form, Button } from 'react-bootstrap';
import classNames from 'classnames';
import LightbulbOutline from 'mdi-react/LightbulbOutlineIcon';

import ModalWrapper from 'modules/shared/ModalWrapper';
import {
  errorReqSelector,
  getOrderPartNumRequest,
  getTripFieldsRequest,
  getTripOrdersRequest,
  setReqDocError,
} from 'modules/common/FillTrip';
import { kioskSelector } from 'modules/kiosk/containers/Kiosk/services/selectors';
import { getOrdersBookRequest, getOrdersOzmRequest } from 'modules/common/BookingModal/actions';
import { PhoneService } from '../../../../entities/Phone/service';
import { ParamName } from '../../../../entities/FillBtn/interfaces';

import { useModal } from '../../hooks';
import Group from '../Group';
import { useChange, useLight } from '../hooks';
import { FormInputFieldProps } from '../types';

import OrdersModal from '../../OrdersModal';
import styles from './Input.module.scss';
import { bookingModalSelector } from '../../../common/BookingModal/selector';

type Props = {
  requestParamName?: ParamName;
  isMask?: boolean;
  noGrowth?: boolean;
  isPosNumber?: boolean;
  isRequired?: boolean;
  isRequiredAtStart?: boolean;
  isOptRequired?: boolean;
  isDisabledBtnDots?: boolean;
  orderInfo?: any;
};

const Input: React.FC<Props & FormInputFieldProps> = React.memo(
  ({
    requestParamName,
    label,
    noGrowth,
    helpText,
    children,
    onFocus,
    onLine = false,
    isMask = false,
    isPosNumber = false,
    isRequired = false,
    isOptRequired = false,
    isRequiredAtStart = false,
    isDisabledBtnDots = false,
    orderInfo,
    ...props
  }) => {
    const dispatch = useDispatch();
    const { isShow: isShowYN, openModal: openModalYN, closeModal: closeModalYN } = useModal();
    const { isShow: isShowBook, closeModal: closeModalBook } = useModal();

    const [isFocus, setIsFocus] = useState<boolean>(false);

    const { response } = useSelector(bookingModalSelector);

    const errorReqData = useSelector(errorReqSelector);
    const { activeInput } = useSelector(kioskSelector);

    const { values: tripValue, setFieldValue } = useFormikContext();
    const [field, meta] = useField(props);
    const { type, maxLength = 0 } = props;
    const { name, value, onBlur } = field;
    const { error, touched } = meta;
    const isInvalid = Boolean(error) && touched;
    const isNumField = type === 'number';
    const isNumMax = type === 'number' && maxLength;
    let invalidChars = ['+', '-', 'e'];

    const { isShow, isSuccess } = useLight(name, value, requestParamName);

    const handleChange = useChange(props);
    if (isPosNumber) {
      invalidChars = [...invalidChars, ',', '.'];
    }

    const getSAPOrdersData = (roleId: number) => {
      dispatch(getOrdersOzmRequest(value, tripValue, roleId));
      openModalYN();
    };

    // @ts-ignore
    useEffect(
      () => () => {
        if (errorReqData) dispatch(setReqDocError(false));
      },
      [],
    );

    const handleFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onFocus) {
        // @ts-ignore
        onFocus(e.target.value);
      }
      setIsFocus(true);
    };

    const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
      onBlur(e);
      setIsFocus(false);

      if (name.includes('phone')) {
        const unformat = PhoneService.unformatPhone(e.target?.value);

        if (!unformat || unformat === '380') setFieldValue(name, '');
      }
    };

    const handleChangeWrapper = (e: React.ChangeEvent<HTMLInputElement>) => {
      const valueInput = e.target.value;
      if (valueInput.length > maxLength) {
        return;
      }

      // @ts-ignore
      handleChange(e);
    };

    const keyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e && (invalidChars.includes(e.key) || ['ArrowUp', 'ArrowDown'].includes(e.code))) {
        e.preventDefault();
      }
    };

    const wheelHandler = (e: React.WheelEvent<HTMLInputElement>) => {
      if (e) {
        e.currentTarget.blur();
      }
    };

    const getFieldsHandler = () => {
      if (requestParamName?.includes('orders')) return dispatch(getTripOrdersRequest(value, tripValue));
      if (requestParamName?.includes('orderPartNum')) return dispatch(getOrderPartNumRequest(value, tripValue));
      if (requestParamName?.includes('bookOrder')) {
        return dispatch(getOrdersBookRequest(value, tripValue, 1));
      }
      if (requestParamName?.includes('modalInput')) return getSAPOrdersData(2);
      if (requestParamName?.includes('number')) return getSAPOrdersData(3);
      if (requestParamName && value) {
        dispatch(getTripFieldsRequest({ fieldName: name, paramName: requestParamName, value, tripValue }));
      }
    };

    return (
      <>
        <Group
          name={name}
          controlId={name}
          label={label}
          error={error}
          markButtons={children}
          isInvalid={isInvalid}
          onLine={onLine}
          isFocus={isFocus}
          isRequired={isRequired}
          isRequiredAtStart={isRequiredAtStart}
          growth={noGrowth}
        >
          {requestParamName ? (
            <div className={classNames('d-flex position-relative w-100', { [styles.inputOptMark]: isOptRequired })}>
              {isShow && (
                <LightbulbOutline
                  className={classNames('position-absolute', styles.light, {
                    'text-success': isSuccess,
                    'text-danger': !isSuccess,
                  })}
                />
              )}
              <Form.Control
                {...props}
                className={classNames(props.className, styles.input, {
                  [styles.inputDots]: !!requestParamName,
                })}
                name={name}
                value={value || ''}
                isInvalid={isInvalid}
                onChange={isNumMax && !isMask ? handleChangeWrapper : handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                autoComplete="off"
                {...(isNumField && { onKeyDown: keyDownHandler })}
                {...(isNumField && { onWheel: wheelHandler })}
              />
              <Button
                variant="light"
                size="sm"
                className={styles.dotsBtn}
                disabled={isDisabledBtnDots}
                onClick={getFieldsHandler}
              >
                ...
              </Button>
            </div>
          ) : (
            <div className={classNames('position-relative w-100', { [styles.inputOptMark]: isOptRequired })}>
              <Form.Control
                {...props}
                className={classNames(
                  props.className,
                  styles.input,
                  {
                    [styles.inputDots]: !!requestParamName,
                  },
                  activeInput === name ? 'bck-yellow' : '',
                )}
                name={name}
                value={value || ''}
                isInvalid={isInvalid}
                onChange={isNumMax && !isMask ? handleChangeWrapper : handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                autoComplete="off"
                {...(isNumField && { onKeyDown: keyDownHandler })}
                {...(isNumField && { onWheel: wheelHandler })}
              />
            </div>
          )}
        </Group>
        <ModalWrapper show={isShowBook} onHide={closeModalBook} className={styles.modalBook}>
          <div>
            <h6 className="text-center">В процесі розробки</h6>
            <h6 className="text-center">Інтеграція з САП не реалізована</h6>
            <div className="d-flex justify-content-center mt-3">
              <Button className="px-5" variant="danger" onClick={closeModalBook}>
                Ok
              </Button>
            </div>
          </div>
        </ModalWrapper>
        <OrdersModal
          className="modalOver"
          isShow={isShowYN}
          order={response}
          closeHandler={closeModalYN}
          closeModal={() => {
            closeModalYN();
          }}
        />
      </>
    );
  },
);

export default Input;
